'use client'

import Cal, { getCalApi } from '@calcom/embed-react'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { cn } from '../utils/cn'

type ParsedCalUrl = {
	namespace: string
	calLink: string
}

type CalLayout = 'month_view' | 'week_view' | 'column_view'

export type CalCalendarProps = {
	url?: string | null
	layout?: CalLayout
	className?: string
	style?: CSSProperties
	config?: Record<string, unknown>
	title?: string
}

const DEFAULT_LAYOUT: CalLayout = 'month_view'

export const CalCalendar = ({ url, layout = DEFAULT_LAYOUT, className, style, config, title }: CalCalendarProps) => {
	const parsed = useMemo(() => parseCalUrl(url), [url])
	const containerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (!parsed) {
			return
		}

		let isMounted = true
		;(async () => {
			try {
				const cal = await getCalApi({ namespace: parsed.namespace })
				if (!isMounted) {
					return
				}
				cal('ui', { hideEventTypeDetails: false, layout })
			} catch (error) {
				console.error('Failed to initialize Cal.com embed', error)
			}
		})()

		return () => {
			isMounted = false
		}
	}, [parsed, layout])

	useEffect(() => {
		if (!parsed) {
			return
		}

		const container = containerRef.current
		if (!container) {
			return
		}

		const applyIframeTweaks = () => {
			const iframe = container.querySelector<HTMLIFrameElement>('iframe')
			if (!iframe) {
				return
			}

			iframe.setAttribute('scrolling', 'no')
			iframe.setAttribute('frameborder', '0')
			iframe.style.setProperty('overflow', 'hidden')
			iframe.style.setProperty('border', '0')
			iframe.style.setProperty('scrollbar-width', 'none')
			iframe.style.setProperty('-ms-overflow-style', 'none')
		}

		applyIframeTweaks()

		if (typeof MutationObserver === 'undefined') {
			return
		}

		const observer = new MutationObserver(() => {
			applyIframeTweaks()
		})

		observer.observe(container, { childList: true, subtree: true })

		return () => {
			observer.disconnect()
		}
	}, [parsed])

	if (!parsed) {
		return (
			<div className={className} style={style}>
				<div
					style={{
						border: '1px dashed #d1d5db',
						borderRadius: 8,
						padding: 16,
						color: '#6b7280',
						fontSize: 14,
					}}
				>
					Add a valid Cal.com link to display the calendar.
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col md:flex-row gap-64">
			{title && <h2 className="text-h3 text-black [:is(.bg-dark-1,.bg-dark-2,.bg-dark-3)_&]:text-white max-w-[230px]">{title}</h2>}
			<div
				ref={containerRef}
				className={cn(
					'overflow-hidden [&_*]:overflow-hidden [&_iframe]:overflow-hidden [&::-webkit-scrollbar]:hidden [&_*::-webkit-scrollbar]:hidden [&_iframe::-webkit-scrollbar]:hidden grow',
					className,
				)}
				style={{
					minHeight: 540,
					...style,
				}}
			>
				<Cal
					namespace={parsed.namespace}
					calLink={parsed.calLink}
					style={{ width: '100%', overflow: 'hidden', border: '0' }}
					config={{
						layout,
						...config,
					}}
				/>
			</div>
		</div>
	)
}

export const parseCalUrl = (url?: string | null): ParsedCalUrl | null => {
	if (!url) {
		return null
	}

	try {
		const parsedUrl = new URL(url)
		const segments = parsedUrl.pathname.split('/').filter(Boolean)
		if (segments.length === 0) {
			return null
		}

		const [namespace, ...rest] = segments
		if (!namespace) {
			return null
		}

		const calLink = [namespace, ...rest].join('/')
		return { namespace, calLink }
	} catch {
		return null
	}
}
